import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye, EyeIcon } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";

import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { RunHistoryFilter } from "./filter";
import { useGetMeterRunHistoryForSchedule } from "@features/mx/readCycleManagement/hooks";

interface RunData {
  id: string;
  runId: string;
  triggeredBy: string;
  runStartDate: string;
  runEndDate: string;
  status: string;
  totalRoutes: number;
  totalMeters: number;
  metersRead: number;
  completionPercentage: string;
  exceptions: number;
  dataCollectionMethodName?: string;
}

const ScheduleRunHistory = () => {
  const { schedule_id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // Read all params from URL including page
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const statusFilter = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  // State for pending status filter
  const [pendingStatusFilter, setPendingStatusFilter] = useState(
    statusFilter || "All Status"
  );

  const { data: meterRunHistory } = useGetMeterRunHistoryForSchedule(
    schedule_id,
    currentPage,
    searchQuery,
    statusFilter
  );

  const statusOptions = useMemo(
    () => [
      { value: "not_dispatched", label: "Not Dispatched" },
      { value: "dispatched", label: "Dispatched" },
      { value: "pending", label: "Pending" },
      { value: "completed", label: "Completed" }
    ],
    []
  );


  // Update pending filter when URL params change
  useEffect(() => {
    setPendingStatusFilter(statusFilter || "All Status");
  }, [statusFilter]);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on filtered data
  useEffect(() => {
    const totalItems = meterRunHistory?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [meterRunHistory?.count, currentPage]);

  // Handle status filter change
  const handleStatusFilterChange = (value: string) => {
    setPendingStatusFilter(value);
  };

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(statusFilter &&
        statusFilter !== "All Status" && { status: statusFilter }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Apply filters
  const handleApplyFilters = () => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(searchQuery && { search: searchQuery }),
    };

    if (pendingStatusFilter !== "All Status") {
      newParams.status = pendingStatusFilter;
    } else {
      delete newParams.status;
    }

    // Reset to page 1 on filter change
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Reset filters
  const handleResetFilters = () => {
    setPendingStatusFilter("All Status");
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.status;
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle view run
  const handleViewRun = (run: RunData) => {
    if (!run.dataCollectionMethodName) {
      toast({
        title: "Data Collection Method Not Found",
        description: "This record is missing a data collection method.",
        variant: "destructive",
      });
      return;
    }
    navigate(`/mx/reads/detail/${run.dataCollectionMethodName.toLowerCase()}/${run.id}`);
  };

  const columns = useMemo<TableColumn<RunData>[]>(
    () => [
      {
        key: "startDate",
        header: "Run Start Date",
        sortable: true,
      },
      {
        key: "endDate",
        header: "Run End Date",
        sortable: true,
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        render: (value) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${value === "Completed"
              ? "bg-green-100 text-green-800"
              : value === "Running"
                ? "bg-blue-100 text-blue-800"
                : value === "Scheduled"
                  ? "bg-gray-100 text-gray-800"
                  : "bg-red-100 text-red-800"
              }`}
          >
            {value}
          </span>
        ),
      },
      {
        key: "meterSchedule",
        header: "Total Routes",
        sortable: true,
        render: (row) => <span>{row?.meterCycle?.routeCount}</span>,
      },
      {
        key: "summary",
        header: "Total Resources",
        sortable: true,
        render: (value) => <span>{value?.meterCount}</span>,
      },
      {
        key: "summary",
        header: "Reads",
        sortable: true,
        render: (value) => <span>{value?.readingCount}</span>,
      },
      {
        key: "completionPercentage",
        header: "Completion %",
        sortable: true,
      },
      {
        key: "summary",
        header: "Exemptions",
        sortable: true,
        render: (value) => <span>{value?.exemptedCount}</span>,
      },
    ],
    []
  );

  const actions: TableAction[] = [
    {
      label: "View",
      icon: <EyeIcon className="text-gray-500" />,
      onClick: (row) => {
        handleViewRun(row);
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Runs History"
        subtitle="Monitor and filter execution history for this read cycle"
      />

      <div className="space-y-4">
        <GenericTable
          data={meterRunHistory?.results || []}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          emptyMessage="No runs found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={
            <RunHistoryFilter
              statusFilter={pendingStatusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              statusOptions={statusOptions}
            />
          }
          filters={
            statusFilter && statusFilter !== "All Status" ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  1 filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default ScheduleRunHistory;
